<template>
  <div class="w-full bg-slate-50 min-h-screen p-4" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-8">
        <h1 class="text-2xl font-black text-slate-800 tracking-tight">Relatório TCE: Veículos</h1>
        <p class="text-sm text-slate-500 font-medium mt-1">Consulta e exportação de dados da frota para o Tribunal de Contas</p>
      </div>

      <!-- ÁREA DE FILTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-custom">
          <div class="px-6 py-4" style="background-color: #333333;">
              <h2 class="text-white font-bold flex items-center gap-2 uppercase tracking-widest text-[11px]">
                  <v-icon color="orange" small class="mr-2">mdi-magnify</v-icon>
                  FILTRAR CONSULTA
              </h2>
          </div>
          <div class="px-6 pt-6 pb-10">
              <v-row>
                <v-col cols="12" sm="3" md="3">
                  <DatePicker
                    @dateValue="[(filter.date = $event)]"
                    label="Mês e Ano:"
                    type="month"
                  />
                </v-col>
                <v-col cols="12" sm="9" md="9">
                  <v-autocomplete
                    :items="prefectures"
                    label="Prefeitura:"
                    v-model="filter.prefecture_id"
                    item-value="prefecture_id"
                    item-text="prefecture_social_name"
                    outlined
                    dense
                    @change="getCode(filter.prefecture_id)"
                  />
                </v-col>
                <v-col cols="12">
                  <v-autocomplete
                    :items="codigos"
                    label="Código da Unidade Gestora:"
                    v-model="filter.cod"
                    item-value="cod"
                    item-text="cod"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>

               <div class="flex justify-end gap-3 mt-8">
                  <v-btn
                    :to="`/report/tce/veiculos/${filter.date?.substring(5)}/${filter.date?.substring(0, 4)}/${filter.prefecture_id}/${filter.cod}`"
                    target="_blank"
                    depressed
                    :disabled="!filter.prefecture_id || !filter.cod || !filter.date"
                    class="bg-slate-100 text-slate-600 font-bold px-6 h-10 rounded-lg transition-all text-[11px] uppercase tracking-widest shadow-none"
                  >
                    Gerar Relatório
                  </v-btn>
                  <button 
                    @click="search"
                    :disabled="isButtonDisabled"
                    class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/20 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-10 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-bold uppercase tracking-widest"
                  >
                      <v-icon color="white" small class="mr-1">mdi-filter-variant</v-icon>
                      FILTRAR
                  </button>
              </div>
          </div>
      </section>

      <!-- RESULTADO -->
      <section v-if="data.length > 0 || loader" class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-custom">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <h2 class="text-white font-bold flex items-center gap-2 uppercase tracking-widest text-[11px]">
                  <v-icon color="orange" small class="mr-2">mdi-table-large</v-icon>
                  RESULTADO
              </h2>
              <button 
                @click="exportFile"
                :disabled="isButtonDisabled"
                class="bg-white/10 hover:bg-white/20 text-white font-bold text-[10px] px-6 py-2 rounded-lg border border-white/20 transition-all uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
              >
                <v-icon small color="white">mdi-download</v-icon>
                Exportar
              </button>
          </div>
          <div class="overflow-x-auto">
              <v-data-table 
                :loading="loader" 
                :items="data" 
                :headers="headers"
                hide-default-footer
                class="custom-table"
              >
                <template v-slot:item.tipoFrota="{ item }">
                  <span class="text-xs font-bold px-2 py-1 rounded bg-slate-100 text-slate-600 uppercase">
                    {{
                      item.tipoFrota == 1
                        ? "Próprio"
                        : item.tipoFrota == 2
                        ? "Locado"
                        : item.tipoFrota == 3
                        ? "Prestação de Serviço"
                        : "Cedido"
                    }}
                  </span>
                </template>
              </v-data-table>
          </div>
      </section>

      <v-snackbar
        v-if="snack"
        multi-line
        :color="snack.color"
        v-model="snack.show"
        transition="fade-transition"
        top
        right
      >
        {{ snack.message }}
        <template v-slot:action="{ attrs }">
          <v-btn dark text v-bind="attrs" @click="snack.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import DatePicker from "@/components/InputDate.vue";
export default {
  components: { DatePicker },
  mixins: [Helpers],
  data() {
    return {
      loader: false,
      data: [],
      prefectures: [],
      codigos: [],
      filter: {
        prefecture_id: null,
        date: null,
        cod: null,
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "Código da Unidade Gestora", value: "codUnidadeGestora" },
        { text: "Placa", value: "placa" },
        { text: "Ano do Modelo", value: "anoModelo" },
        { text: "Renavan", value: "numeroRenavan" },
        { text: "Número do Modelo", value: "numeroModelo" },
        { text: "Tipo de Frota", value: "tipoFrota" },
        { text: "Proprietário do Veículo", value: "cpfcnpjProprietario" },
        { text: "Locador ou Prestador de Serviço", value: "cpfcnpjLocador" },
      ],
    };
  },
  created() {
    this.Helpers;
    this.initialize();
  },
  computed: {
    isButtonDisabled() {
      return !(
        this.filter.prefecture_id &&
        this.filter.date &&
        this.filter.cod
      );
    },
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http.get("/prefectures").then((r) => {
        this.prefectures = r.data;
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      }).catch(() => {
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
        if (this.snack) {
          this.snack.message = "Erro ao carregar prefeituras";
          this.snack.color = "error";
          this.snack.show = true;
        }
      });
    },
    getCode(prefecture_id) {
      this.$http(`/tce/codigo/${prefecture_id}`).then(
        (r) => (this.codigos = r.data)
      );
    },
    search() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/tce/veiculos/${this.filter.prefecture_id}/${this.filter.cod}`)
        .then((res) => {
          this.data = res.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          if (this.snack) {
            this.snack.message = e.message || "Erro ao filtrar dados";
            this.snack.color = "error";
            this.snack.show = true;
          }
        });
    },
    exportFile() {
      const element = document.createElement("a");
      const content = this.data.map((value, index) => {
        if (index > 0) {
          return `\n${value.export}`;
        }
        return value.export;
      });

      const file = new Blob(content, { type: "text/plain" });
      element.href = URL.createObjectURL(file);
      element.download = `${this.filter.cod}${this.filter.date.substring(
        5
      )}${this.filter.date.substring(0, 4)}Veiculos.txt`;
      document.body.appendChild(element);
      element.click();
    },
  },
};
</script>
